import {Context, EMPTY_CONTEXT, Options, Schema} from './schema';

export interface NumberOptions extends Options<number | null> {
  min?: number | ((context?: Context) => number | null);
  max?: number | ((context?: Context) => number | null);
  isInteger?: boolean;
}

export class NumberSchema extends Schema<number | null, NumberOptions> {
  constructor(options: NumberOptions = {}) {
    super(options);
  }

  public toJSON(): object {
    return {
      type: 'NumberSchema',
      ...super.toJSON(),
      min: this._options.min,
      max: this._options.max,
    };
  }

  public min(context: Context = EMPTY_CONTEXT): number | null {
    return this._options.min === undefined
      ? null
      : typeof this._options.min === 'function'
        ? this._options.min(context)
        : this._options.min;
  }

  public max(context: Context = EMPTY_CONTEXT): number | null {
    return this._options.max === undefined
      ? null
      : typeof this._options.max === 'function'
        ? this._options.max(context)
        : this._options.max;
  }

  public valueToMobx(value: number | null): number | null {
    return this._nullifyValue(value);
  }

  public valueToJS(
    value: number | null,
    context: Context = EMPTY_CONTEXT,
  ): number | null {
    return this.isComputed
      ? this.computedValue(context)
      : this._nullifyValue(value);
  }

  // Reject `NaN` values
  public typeIsValid(value: any): value is number | null {
    return value === null || (typeof value === 'number' && value === value);
  }

  public valueIsValid(
    value: number | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      this.requiredIsValid(value, context) &&
      this.minIsValid(value, context) &&
      this.maxIsValid(value, context) &&
      this.customFunctionIsValid(value, context)
    );
  }

  public minIsValid(
    value: number | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    const min = this.min(context);
    return value === null || min === null || value >= min;
  }

  public maxIsValid(
    value: number | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    const max = this.max(context);
    return value === null || max === null || value <= max;
  }
}
